import React, { useCallback, useEffect, useRef, useState, VFC } from 'react';
import { AddStrongIcon, ChevronRightRegularIcon, CloseSmallIcon, SearchStrongIcon } from 'src/monet/icons';
import { functionNoop } from 'src/utils/function/noop';
import { Nullable } from 'src/types/nullable.type';
import classNames from 'classnames';
import { Filters, useFilterBarParams } from './use-params';
import { MultiSelectWithSearch } from 'src/common/multi-select/with-search';
import produce from 'immer';
import { Button } from 'src/common/button';
import { JourneyMenu } from 'src/common/JourneyMenu';
import { IconButton } from 'src/common/icon-button';

interface FilterBarProps {
  onSearchInputChange?: (searchInputValue: string) => void;
  searchPlaceholder?: string;
  widthClassname?: string;
  filterSpecList?: FilterSpec[];
  hasSearch?: boolean;
  isDisabled?: boolean;
}

export type FilterType = 'single-select' | 'multi-select';

export type FilterSpec = {
  id: string;
  label: string;
  type: FilterType;
  searchable?: boolean;
  searchPlaceholder?: string;
  options: {
    label: string;
    value: string;
  }[];
};

function flattenFilters(filters: Filters): { name: string; value: string }[] {
  return Object.entries(filters).flatMap(([name, values]) => values.map((v) => ({ name, value: v })));
}

export const FilterBar = ({
  onSearchInputChange,
  widthClassname,
  searchPlaceholder,
  filterSpecList = [],
  isDisabled,
  hasSearch = true,
}: FilterBarProps) => {
  const {
    filterBarParams: { search_term, filters },
    paramsReadyInternal,
    updateSearchTerm,
    updateFilters,
  } = useFilterBarParams();
  const [showSearchInput, setShowSearchInput] = useState(false);
  const searchInputRef = useRef<Nullable<HTMLInputElement>>(null);
  const filterMenuOpenRef = useRef(functionNoop);
  const filterMenuCloseRef = useRef(functionNoop);
  const [activeFilterId, setActiveFilterId] = useState('');
  const [activeFilterSearchTerm, setActiveFilterSearchTerm] = useState('');

  const onSearchIconClicked = () => {
    setShowSearchInput(true);
  };

  useEffect(() => {
    if (paramsReadyInternal) {
      if (searchInputRef.current) {
        searchInputRef.current.value = search_term;
      }
    }
  }, [paramsReadyInternal]);

  useEffect(() => {
    setShowSearchInput(searchInputRef.current === document.activeElement || !!search_term);
  }, [paramsReadyInternal, search_term]);

  useEffect(() => {
    const { current } = searchInputRef;
    const onInputBlurred = () => {
      if (current && current.value === '') {
        setShowSearchInput(false);
      }
    };

    if (showSearchInput) {
      current?.focus();
    }

    current?.addEventListener('blur', onInputBlurred);

    return () => {
      current?.removeEventListener('blur', onInputBlurred);
      updateSearchTerm.cancel();
    };
  }, [showSearchInput, updateSearchTerm]);

  const filterSubMenuOpenRef = useRef(functionNoop);
  const filterSubMenuCloseRef = useRef(functionNoop);

  const onClose = useCallback(() => {
    setActiveFilterId('');
    setActiveFilterSearchTerm('');
  }, []);

  const onBack = useCallback(() => {
    setActiveFilterId('');
    setActiveFilterSearchTerm('');
    setTimeout(() => {
      filterMenuOpenRef.current();
    }, 50);
  }, []);

  const renderOrigin = () => {
    return (
      <div className={hasSearch ? 'pl-md' : ''}>
        <Button disabled={isDisabled} size='small' variant='tertiary' icon={<AddStrongIcon />} label='Filter' />
      </div>
    );
  };

  const onFilterSearchInputChanged = useCallback((searchInput: string) => {
    setActiveFilterSearchTerm(searchInput);
  }, []);

  const onItemSelected = useCallback(
    (selectedItem: { value: string; label: string }) => {
      if (!activeFilterId) {
        return;
      }
      const activeFilterSpec = filterSpecList.find((fs) => fs.id === activeFilterId);

      if (!activeFilterSpec) {
        return;
      }

      updateFilters(
        produce(filters, (state: Filters) => {
          if (state[activeFilterId]) {
            if (activeFilterSpec.type === 'single-select') {
              state[activeFilterId] = [selectedItem.value];
            } else if (activeFilterSpec.type === 'multi-select') {
              state[activeFilterId].push(selectedItem.value);
            }
          } else {
            state[activeFilterId] = [selectedItem.value];
          }
        })
      );
    },
    [filterSpecList, activeFilterId, filters, updateFilters]
  );

  const onItemDeselected = useCallback(
    (selectedItem: { value: string; label: string }) => {
      if (!activeFilterId) {
        return;
      }
      const activeFilterSpec = filterSpecList.find((fs) => fs.id === activeFilterId);

      if (!activeFilterSpec) {
        return;
      }

      updateFilters(
        produce(filters, (state: Filters) => {
          if (state[activeFilterId]) {
            if (activeFilterSpec.type === 'single-select') {
              delete state[activeFilterId];
            } else if (activeFilterSpec.type === 'multi-select') {
              const index = state[activeFilterId].findIndex((v) => v === selectedItem.value);
              if (index !== -1) {
                state[activeFilterId].splice(index, 1);
              }
              if (state[activeFilterId].length === 0) {
                delete state[activeFilterId];
              }
            }
          }
        })
      );
    },
    [filterSpecList, activeFilterId, filters, updateFilters]
  );

  const renderFilterPanel = () => (
    <JourneyMenu
      placement='bottom-start'
      openRef={filterMenuOpenRef}
      closeRef={filterMenuCloseRef}
      onClose={onClose}
      renderOrigin={renderOrigin}
      containerClassName='gap-2'
    >
      {filterSpecList.map((filterSpec: FilterSpec) => {
        const onClick = () => {
          setActiveFilterId(filterSpec.id);
        };
        return (
          <JourneyMenu.Item key={filterSpec.id} onClick={onClick}>
            <div className='flex items-center space-x-sm'>
              <span className='text-bedrock-p text-bedrock-black'>{filterSpec.label}</span>
            </div>
            <ChevronRightRegularIcon className='ml-auto' />
          </JourneyMenu.Item>
        );
      })}
    </JourneyMenu>
  );

  const renderActiveFilterPanel = () => {
    if (!activeFilterId) {
      return;
    }
    const activeFilterSpec = filterSpecList?.find((f: FilterSpec) => f.id === activeFilterId);
    if (!activeFilterSpec) {
      return;
    }
    const items = activeFilterSpec.options;
    const activeFilter = filters[activeFilterSpec.id];
    const activeFilterItems = items.filter((fi) => fi.label.toLowerCase().includes(activeFilterSearchTerm));

    const selectedItems = activeFilterItems.filter((fi) => (activeFilter || []).includes(fi.value));

    return (
      <MultiSelectWithSearch
        name='filter-bar'
        searchPlaceholder={activeFilterSpec.searchPlaceholder}
        items={activeFilterItems}
        placement='bottom'
        selectedItems={selectedItems}
        className='pb-4'
        openRef={filterSubMenuOpenRef}
        onClose={onClose}
        onBack={onBack}
        hasBackButton
        closeRef={filterSubMenuCloseRef}
        hideActionOnNotFound
        useStrictValueComparison
        renderOrigin={renderOrigin}
        renderNotFound={() => <span>No results found</span>}
        onSearchInputChange={onFilterSearchInputChanged}
        onItemSelect={onItemSelected}
        onItemDeselect={onItemDeselected}
      />
    );
  };

  return (
    <>
      {hasSearch && (
        <div className='pl-md flex items-center text-bedrock-black border-l border-bedrock-gray-medium'>
          {!showSearchInput ? (
            <IconButton onClick={onSearchIconClicked}>
              <SearchStrongIcon />
            </IconButton>
          ) : (
            <input
              ref={searchInputRef}
              type='text'
              defaultValue={search_term}
              onChange={(event) => {
                updateSearchTerm(event.target.value);
                onSearchInputChange && onSearchInputChange(event.target.value);
              }}
              className={classNames(
                'focus:outline-none focus:ring-0 border-0 text-bedrock-p-strong h-[22px] placeholder:text-bedrock-p-strong placeholder:text-bedrock-dark-gray',
                widthClassname || 'w-[137px]'
              )}
              placeholder={searchPlaceholder || 'Search Journeys'}
            />
          )}
        </div>
      )}
      {filterSpecList.length > 0 && (
        <div className='flex space-x-md'>
          {filterSpecList.length > 0 && (activeFilterId ? renderActiveFilterPanel() : renderFilterPanel())}
          {flattenFilters(filters).length > 0 && (
            <div className='flex items-center gap-2'>
              {flattenFilters(filters).map((fi) => {
                const onClose = () => {
                  updateFilters(
                    produce(filters, (state: Filters) => {
                      const selectedFilterValues = state[fi.name];
                      if (selectedFilterValues) {
                        const index = selectedFilterValues.findIndex((v) => v == fi.value);
                        if (index !== -1) {
                          selectedFilterValues.splice(index, 1);
                        }
                        if (selectedFilterValues.length == 0) {
                          delete state[fi.name];
                        }
                      }
                    })
                  );
                };
                return (
                  <div className='flex flex-row items-center gap-1 border rounded-md bg-neue-canvas pr-2'>
                    <Button
                      size='small'
                      variant='tertiary'
                      label={`${filterSpecList.find((fs) => fs.id === fi.name)?.label}: ${
                        filterSpecList
                          .find((fs) => fs.id === fi.name)
                          ?.options.find((fso) => `${fso.value}` == fi.value)?.label
                      }`}
                      icon={<CloseSmallIcon className='w-5 h-5 shrink-0' />}
                      onClick={onClose}
                    />
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </>
  );
};
